import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatTL } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Check, X, ShoppingBag, Package, Users, DollarSign,
  RefreshCw, Calendar, Banknote, Layers, Settings2, Edit2, Save, CreditCard, Eye,
  BarChart3, BadgePercent, Megaphone,
} from "lucide-react";
import OrderDetailModal from "@/components/OrderDetailModal";
import ReportsTab from "@/components/admin/ReportsTab";
import ProductsTab from "@/components/admin/ProductsTab";
import DealerPrices from "@/components/admin/DealerPrices";
import { CampaignsTab, AnnouncementsTab } from "@/components/admin/PromoTabs";
import { ExportButton } from "@/components/admin/ExportButton";

const PAYMENT_OPTIONS = [
  { id: "stripe", label: "Kredi Kartı" },
  { id: "havale", label: "Havale / EFT" },
  { id: "cari", label: "Cari Hesaptan Düş" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [cats, setCats] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [jobs, setJobs] = useState([]);

  const refresh = async () => {
    const [s, o, c, d, g, cu, sl, j] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/orders"),
      api.get("/categories"),
      api.get("/admin/dealers"),
      api.get("/admin/dealer-groups"),
      api.get("/admin/currencies"),
      api.get("/admin/sync-logs"),
      api.get("/admin/scheduler-jobs"),
    ]);
    setStats(s.data); setOrders(o.data); setCats(c.data);
    setDealers(d.data); setGroups(g.data); setCurrencies(cu.data);
    setSyncLogs(sl.data); setJobs(j.data.jobs || []);
  };

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  if (loading) return <div className="kz-container py-20 text-center">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris" replace/>;
  if (user.role !== "admin") return <Navigate to="/" replace/>;

  const TABS = [
    { id: "dashboard", label: "Özet", Icon: Settings2 },
    { id: "reports", label: "Raporlar", Icon: BarChart3 },
    { id: "orders", label: `Siparişler (${orders.length})`, Icon: ShoppingBag },
    { id: "products", label: `Ürünler (${stats.products || 0})`, Icon: Package },
    { id: "dealers", label: `Bayiler (${stats.approved_dealers || 0} onaylı · ${dealers.filter(d=>d.b2b_status==="pending").length} bekliyor)`, Icon: Users },
    { id: "groups", label: "Bayi Grupları", Icon: Layers },
    { id: "campaigns", label: "Kampanyalar", Icon: BadgePercent },
    { id: "announcements", label: "Duyurular", Icon: Megaphone },
    { id: "currencies", label: "Döviz", Icon: Banknote },
    { id: "payments", label: "Ödeme Ayarları", Icon: CreditCard },
    { id: "sync", label: "Senkron", Icon: RefreshCw },
  ];

  return (
    <div className="kz-container py-10">
      <h1 className="font-display text-3xl md:text-4xl font-black mb-2">Yönetim Paneli</h1>
      <p className="text-ink-500 mb-6">Kuzey Yapı Hırdavat — admin@kuzeyyapi.tr</p>

      <div className="flex gap-1 mb-6 border-b border-ink-200 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 font-semibold text-sm border-b-2 whitespace-nowrap flex items-center gap-1.5 ${
              tab === t.id ? "border-ink-900 text-ink-900" : "border-transparent text-ink-500 hover:text-ink-900"
            }`}
          ><t.Icon size={14}/> {t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && <DashboardTab stats={stats} jobs={jobs}/>}
      {tab === "reports" && <ReportsTab/>}
      {tab === "orders" && <OrdersTab orders={orders} refresh={refresh}/>}
      {tab === "products" && <ProductsTab cats={cats} onChanged={refresh}/>}
      {tab === "campaigns" && <CampaignsTab cats={cats}/>}
      {tab === "announcements" && <AnnouncementsTab/>}
      {tab === "dealers" && <DealersTab dealers={dealers} groups={groups} refresh={refresh}/>}
      {tab === "groups" && <GroupsTab groups={groups} refresh={refresh}/>}
      {tab === "currencies" && <CurrenciesTab currencies={currencies} refresh={refresh}/>}
      {tab === "payments" && <PaymentSettingsTab/>}
      {tab === "sync" && <SyncTab jobs={jobs} logs={syncLogs} refresh={refresh}/>}
    </div>
  );
}

function DashboardTab({ stats, jobs }) {
  const cards = [
    { id: "revenue", label: "Toplam Gelir", value: formatTL(stats.revenue || 0), Icon: DollarSign, color: "text-green-600" },
    { id: "orders", label: "Toplam Sipariş", value: stats.total_orders || 0, Icon: ShoppingBag, color: "text-brand" },
    { id: "approved", label: "Onaylı Bayi", value: stats.approved_dealers || 0, Icon: Users, color: "text-b2b" },
    { id: "pending", label: "Bekleyen Bayi", value: stats.pending_dealers || 0, Icon: Users, color: "text-yellow-600" },
    { id: "products", label: "Toplam Ürün", value: stats.products || 0, Icon: Package, color: "text-ink-700" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {cards.map((s) => (
          <div key={s.id} className="kz-card p-5">
            <s.Icon className={s.color + " mb-2"}/>
            <div className="text-xs uppercase tracking-wider text-ink-500">{s.label}</div>
            <div className="font-display font-black text-2xl">{s.value}</div>
          </div>
        ))}
      </div>
      <div className="kz-card p-5">
        <h3 className="font-display font-black mb-3 flex items-center gap-2"><Calendar size={16}/> Otomatik Senkron Programı</h3>
        {jobs.length === 0 ? (
          <div className="text-sm text-ink-500">Henüz programlı görev yok</div>
        ) : (
          <div className="space-y-2">
            {jobs.map(j => (
              <div key={j.id} className="flex justify-between text-sm border-b border-ink-100 pb-2 last:border-0">
                <span className="font-semibold">{j.name}</span>
                <span className="text-ink-500 font-mono text-xs">Sonraki: {j.next_run}</span>
              </div>
            ))}
          </div>
        )}
        {stats.last_sync && (
          <div className="mt-4 pt-3 border-t border-ink-200 text-xs text-ink-500">
            Son senkron: <span className="font-semibold text-ink-900">{stats.last_sync.type}</span> ·{" "}
            {stats.last_sync.products !== undefined && `${stats.last_sync.products} ürün · `}
            {new Date(stats.last_sync.at).toLocaleString("tr-TR")}
          </div>
        )}
      </div>
    </div>
  );
}

function OrdersTab({ orders, refresh }) {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const updateStatus = async (id, params) => {
    await api.put(`/admin/orders/${id}/status`, null, { params });
    toast.success("Sipariş güncellendi"); refresh();
  };
  const del = async (id) => {
    if (!window.confirm("Bu siparişi silmek istediğinizden emin misiniz? Cari ödemeli ise bakiye iade edilir.")) return;
    await api.delete(`/admin/orders/${id}`);
    toast.success("Sipariş silindi"); refresh();
  };
  return (
    <>
      <div className="flex justify-end mb-3">
        <ExportButton path="/admin/export/orders" filename="siparisler.xlsx" label="Tüm Siparişleri Excel'e Aktar" testid="export-orders"/>
      </div>
      <div className="kz-card overflow-x-auto">
        <table className="w-full">
          <thead className="bg-ink-100">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="p-3">Sipariş</th><th className="p-3">Müşteri</th><th className="p-3">Tarih</th>
              <th className="p-3">Tutar</th><th className="p-3">Ödeme</th><th className="p-3">Durum</th><th className="p-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan="7" className="p-10 text-center text-ink-500">Sipariş yok</td></tr>}
            {orders.map((o, i) => (
              <tr key={o.id} className={i % 2 ? "bg-ink-100/50" : ""}>
                <td className="p-3"><button onClick={()=>setSelectedOrder(o)} className="font-mono text-xs hover:text-brand">{o.order_number}</button></td>
                <td className="p-3 text-sm">{o.user_name}<br/><span className="text-xs text-ink-500">{o.user_email}</span></td>
                <td className="p-3 text-xs">{new Date(o.created_at).toLocaleDateString("tr-TR")}</td>
                <td className="p-3 font-bold">{formatTL(o.total)}</td>
                <td className="p-3">
                  <div className="text-[10px] text-ink-500 uppercase mb-1">{o.payment_method}</div>
                  <select data-testid={`order-payment-status-${o.id}`} value={o.payment_status} onChange={(e)=>updateStatus(o.id, { payment_status: e.target.value })}
                    className={`text-xs border px-2 py-1 font-bold ${o.payment_status === "paid" ? "border-green-300 bg-green-50 text-green-700" : "border-yellow-300 bg-yellow-50 text-yellow-700"}`}>
                    <option value="pending">Bekliyor</option><option value="paid">Ödendi</option>
                    <option value="failed">Başarısız</option><option value="cancelled">İptal</option>
                  </select>
                </td>
                <td className="p-3">
                  <select data-testid={`order-status-${o.id}`} value={o.order_status} onChange={(e)=>updateStatus(o.id, { status: e.target.value })} className="text-xs border border-ink-200 px-2 py-1">
                    <option value="pending">Beklemede</option>
                    <option value="confirmed">Onaylandı</option>
                    <option value="shipped">Kargoda</option>
                    <option value="delivered">Teslim Edildi</option>
                    <option value="cancelled">İptal</option>
                  </select>
                </td>
                <td className="p-3 flex gap-1">
                  <button data-testid={`view-order-${o.id}`} onClick={()=>setSelectedOrder(o)} className="text-ink-500 hover:text-ink-900 p-1"><Eye size={16}/></button>
                  <button data-testid={`delete-order-${o.id}`} onClick={()=>del(o.id)} className="text-red-600 hover:text-red-800 p-1"><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={()=>setSelectedOrder(null)}/>}
    </>
  );
}

function DealersTab({ dealers, groups, refresh }) {
  const [editing, setEditing] = useState(null);

  const approve = async (id) => { await api.post(`/admin/dealers/${id}/approve`); toast.success("Onaylandı"); refresh(); };
  const reject = async (id) => { await api.post(`/admin/dealers/${id}/reject`); toast.success("Reddedildi"); refresh(); };
  const del = async (id) => {
    if (!window.confirm("Bu bayiyi silmek istediğinizden emin misiniz? Cari geçmişi de silinir.")) return;
    await api.delete(`/admin/dealers/${id}`);
    toast.success("Bayi silindi"); refresh();
  };

  return (
    <div className="space-y-3">
      {dealers.length === 0 && <div className="kz-card p-10 text-center text-ink-500">Bayi bulunamadı</div>}
      {dealers.map(d => (
        <div key={d.id} className="kz-card p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-bold flex items-center gap-2">{d.company_name || d.full_name}
                <span className={`text-[10px] px-2 py-0.5 font-bold ${d.b2b_status === "approved" ? "bg-green-100 text-green-700" : d.b2b_status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                  {d.b2b_status?.toUpperCase()}
                </span>
              </div>
              <div className="text-xs text-ink-500 mt-1">{d.full_name} · {d.email} · VKN: {d.tax_number || "-"}</div>
              <div className="text-xs text-ink-500">{d.phone} {d.address ? " · " + d.address : ""}</div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs">
                <span><b>İskonto:</b> %{d.b2b_discount || 0}</span>
                <span><b>Cari:</b> <span className={d.credit_balance < 0 ? "text-red-600 font-bold" : "text-green-600 font-bold"}>{formatTL(d.credit_balance || 0)}</span></span>
                <span><b>Limit:</b> {formatTL(d.credit_limit || 0)}</span>
                <span><b>Grup:</b> {groups.find(g => g.id === d.dealer_group_id)?.name || "—"}</span>
                <span><b>Ödeme:</b> {(d.allowed_payment_methods || []).map(m => PAYMENT_OPTIONS.find(o => o.id === m)?.label || m).join(", ") || "—"}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              {d.b2b_status === "pending" && (
                <>
                  <button data-testid={`approve-dealer-${d.id}`} onClick={() => approve(d.id)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 text-xs font-bold flex items-center gap-1"><Check size={14}/> Onayla</button>
                  <button data-testid={`reject-dealer-${d.id}`} onClick={() => reject(d.id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 text-xs font-bold flex items-center gap-1"><X size={14}/> Reddet</button>
                </>
              )}
              <button data-testid={`edit-dealer-${d.id}`} onClick={() => setEditing(editing === d.id ? null : d.id)} className="kz-btn-outline text-xs py-1.5 px-3"><Edit2 size={12}/> Düzenle</button>
              <button data-testid={`delete-dealer-${d.id}`} onClick={() => del(d.id)} className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1 px-3 py-1.5 border border-red-200"><Trash2 size={14}/> Sil</button>
            </div>
          </div>
          {editing === d.id && (
            <DealerEditForm dealer={d} groups={groups} onSaved={() => { setEditing(null); refresh(); }} onCancel={() => setEditing(null)}/>
          )}
        </div>
      ))}
    </div>
  );
}

function DealerEditForm({ dealer, groups, onSaved, onCancel }) {
  const [form, setForm] = useState({
    b2b_discount: dealer.b2b_discount || 0,
    credit_balance: dealer.credit_balance || 0,
    credit_limit: dealer.credit_limit || 0,
    dealer_group_id: dealer.dealer_group_id || "",
    allowed_payment_methods: dealer.allowed_payment_methods || ["stripe", "havale", "cari"],
    b2b_status: dealer.b2b_status,
  });
  const [cariAmount, setCariAmount] = useState(0);
  const [cariDesc, setCariDesc] = useState("Manuel ödeme/tahsilat");

  const togglePayment = (id) => setForm(f => ({
    ...f,
    allowed_payment_methods: f.allowed_payment_methods.includes(id) ? f.allowed_payment_methods.filter(x => x !== id) : [...f.allowed_payment_methods, id],
  }));

  const onGroupChange = (gid) => {
    const g = groups.find(x => x.id === gid);
    setForm(f => ({ ...f, dealer_group_id: gid, ...(g ? { b2b_discount: g.default_discount, credit_limit: g.credit_limit, allowed_payment_methods: g.allowed_payment_methods } : {}) }));
  };

  const save = async () => {
    await api.put(`/admin/dealers/${dealer.id}`, {
      ...form,
      b2b_discount: parseFloat(form.b2b_discount),
      credit_balance: parseFloat(form.credit_balance),
      credit_limit: parseFloat(form.credit_limit),
      dealer_group_id: form.dealer_group_id || null,
    });
    toast.success("Güncellendi"); onSaved();
  };

  const applyCariAdjust = async () => {
    const amt = parseFloat(cariAmount);
    if (!amt) { toast.error("Tutar giriniz"); return; }
    await api.post(`/admin/dealers/${dealer.id}/cari-adjust`, null, { params: { amount: amt, description: cariDesc } });
    toast.success("Cari hesap güncellendi"); onSaved();
  };

  return (
    <div className="mt-4 pt-4 border-t border-ink-200 grid grid-cols-1 md:grid-cols-2 gap-3">
      <label className="block">
        <span className="text-xs font-semibold mb-1 block">Bayi Grubu</span>
        <select value={form.dealer_group_id} onChange={(e)=>onGroupChange(e.target.value)} className="w-full border border-ink-200 px-3 py-2 text-sm">
          <option value="">— Grup yok (özel ayarlar) —</option>
          {groups.map(g => <option key={g.id} value={g.id}>{g.name} (%{g.default_discount})</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs font-semibold mb-1 block">İskonto (%)</span>
        <input data-testid={`dealer-discount-${dealer.id}`} type="number" step="0.5" value={form.b2b_discount} onChange={(e)=>setForm({...form, b2b_discount: e.target.value})} className="w-full border border-ink-200 px-3 py-2"/>
      </label>
      <label className="block">
        <span className="text-xs font-semibold mb-1 block">Cari Bakiye (₺)</span>
        <input type="number" step="0.01" value={form.credit_balance} onChange={(e)=>setForm({...form, credit_balance: e.target.value})} className="w-full border border-ink-200 px-3 py-2"/>
      </label>
      <label className="block">
        <span className="text-xs font-semibold mb-1 block">Kredi Limiti (₺)</span>
        <input type="number" step="0.01" value={form.credit_limit} onChange={(e)=>setForm({...form, credit_limit: e.target.value})} className="w-full border border-ink-200 px-3 py-2"/>
      </label>
      <div className="md:col-span-2">
        <div className="text-xs font-semibold mb-2">İzin Verilen Ödeme Yöntemleri</div>
        <div className="flex flex-wrap gap-2">
          {PAYMENT_OPTIONS.map(o => (
            <label key={o.id} className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm ${form.allowed_payment_methods.includes(o.id) ? "border-ink-900 bg-ink-900 text-white" : "border-ink-200"}`}>
              <input type="checkbox" checked={form.allowed_payment_methods.includes(o.id)} onChange={() => togglePayment(o.id)}/>
              {o.label}
            </label>
          ))}
        </div>
      </div>
      <label className="block md:col-span-2">
        <span className="text-xs font-semibold mb-1 block">Onay Durumu</span>
        <select value={form.b2b_status} onChange={(e)=>setForm({...form, b2b_status: e.target.value})} className="w-full border border-ink-200 px-3 py-2 text-sm">
          <option value="pending">Beklemede</option><option value="approved">Onaylı</option>
          <option value="rejected">Reddedildi</option><option value="none">Bayi Değil</option>
        </select>
      </label>
      <div className="md:col-span-2 flex gap-2 justify-end">
        <button onClick={onCancel} className="kz-btn-outline text-sm py-2 px-4">İptal</button>
        <button data-testid={`dealer-save-${dealer.id}`} onClick={save} className="kz-btn-primary text-sm py-2 px-4"><Save size={14}/> Kaydet</button>
      </div>

      <div className="md:col-span-2 text-xs bg-b2b/10 border border-b2b/30 px-3 py-2">
        Kullanılabilir cari limit (bakiye + kredi limiti): <b data-testid={`dealer-available-${dealer.id}`}>{formatTL((parseFloat(form.credit_balance) || 0) + (parseFloat(form.credit_limit) || 0))}</b> — bu tutarı aşan cari siparişler engellenir.
      </div>

      <DealerPrices dealerId={dealer.id}/>

      <div className="md:col-span-2 pt-4 border-t border-ink-200">
        <div className="text-xs font-bold uppercase mb-2">Hızlı Cari İşlem (Ödeme / Tahsilat)</div>
        <div className="flex gap-2 flex-wrap">
          <input type="number" step="0.01" value={cariAmount} onChange={(e)=>setCariAmount(e.target.value)} placeholder="Tutar (+ alacak, - borç)" className="border border-ink-200 px-3 py-2 flex-1 min-w-[140px]"/>
          <input value={cariDesc} onChange={(e)=>setCariDesc(e.target.value)} placeholder="Açıklama" className="border border-ink-200 px-3 py-2 flex-1 min-w-[200px]"/>
          <button onClick={applyCariAdjust} className="kz-btn-dark text-sm py-2 px-4">Uygula</button>
        </div>
      </div>
    </div>
  );
}

function GroupsTab({ groups, refresh }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", description: "", default_discount: 0, allowed_payment_methods: ["stripe", "havale", "cari"], credit_limit: 0 };
  const [form, setForm] = useState(empty);

  const startEdit = (g) => { setEditing(g.id); setForm({ ...g }); setShow(true); };
  const togglePayment = (id) => setForm(f => ({ ...f, allowed_payment_methods: f.allowed_payment_methods.includes(id) ? f.allowed_payment_methods.filter(x=>x!==id) : [...f.allowed_payment_methods, id] }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, default_discount: parseFloat(form.default_discount), credit_limit: parseFloat(form.credit_limit) };
    if (editing) await api.put(`/admin/dealer-groups/${editing}`, payload);
    else await api.post("/admin/dealer-groups", payload);
    toast.success(editing ? "Grup güncellendi" : "Grup oluşturuldu");
    setShow(false); setEditing(null); setForm(empty); refresh();
  };

  const del = async (id) => {
    if (!window.confirm("Bu grubu silmek istediğinizden emin misiniz?")) return;
    await api.delete(`/admin/dealer-groups/${id}`);
    toast.success("Grup silindi"); refresh();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { setEditing(null); setForm(empty); setShow(!show); }} className="kz-btn-primary"><Plus size={16}/> Yeni Grup</button>
      </div>

      {show && (
        <form onSubmit={submit} className="kz-card p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input required placeholder="Grup Adı" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="border border-ink-200 px-3 py-2 md:col-span-2"/>
          <input placeholder="Açıklama" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="border border-ink-200 px-3 py-2 md:col-span-2"/>
          <label className="block">
            <span className="text-xs font-semibold mb-1 block">Varsayılan İskonto (%)</span>
            <input type="number" step="0.5" value={form.default_discount} onChange={(e)=>setForm({...form, default_discount: e.target.value})} className="w-full border border-ink-200 px-3 py-2"/>
          </label>
          <label className="block">
            <span className="text-xs font-semibold mb-1 block">Kredi Limiti (₺)</span>
            <input type="number" step="0.01" value={form.credit_limit} onChange={(e)=>setForm({...form, credit_limit: e.target.value})} className="w-full border border-ink-200 px-3 py-2"/>
          </label>
          <div className="md:col-span-2">
            <div className="text-xs font-semibold mb-2">İzin Verilen Ödeme Yöntemleri</div>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_OPTIONS.map(o => (
                <label key={o.id} className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm ${form.allowed_payment_methods.includes(o.id) ? "border-ink-900 bg-ink-900 text-white" : "border-ink-200"}`}>
                  <input type="checkbox" checked={form.allowed_payment_methods.includes(o.id)} onChange={() => togglePayment(o.id)}/>
                  {o.label}
                </label>
              ))}
            </div>
          </div>
          <button type="submit" className="kz-btn-primary md:col-span-2">{editing ? "Güncelle" : "Oluştur"}</button>
        </form>
      )}

      <div className="kz-card divide-y divide-ink-200">
        {groups.length === 0 ? <div className="p-10 text-center text-ink-500">Henüz grup yok</div> :
          groups.map(g => (
            <div key={g.id} className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-bold">{g.name}</div>
                <div className="text-xs text-ink-500">{g.description}</div>
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  <span><b>İskonto:</b> %{g.default_discount}</span>
                  <span><b>Limit:</b> {formatTL(g.credit_limit)}</span>
                  <span><b>Ödeme:</b> {g.allowed_payment_methods.map(m => PAYMENT_OPTIONS.find(o => o.id === m)?.label).join(", ")}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(g)} className="kz-btn-outline text-xs py-1.5 px-3"><Edit2 size={12}/> Düzenle</button>
                <button onClick={() => del(g.id)} className="text-red-600 hover:text-red-800 text-xs flex items-center gap-1 border border-red-200 px-3 py-1.5"><Trash2 size={14}/> Sil</button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

function CurrenciesTab({ currencies, refresh }) {
  const [syncing, setSyncing] = useState(false);
  const [edits, setEdits] = useState({});

  const syncFromSGS = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/sync-currencies", null, { timeout: 60000 });
      toast.success(`Kurlar güncellendi: ${r.data.rates.map(x=>`${x.code} ₺${x.rate}`).join(", ")}`);
      refresh();
    } catch (err) {
      toast.error("Kur güncellenemedi: " + (err?.response?.data?.detail || err.message));
    } finally { setSyncing(false); }
  };

  const save = async (c) => {
    const newRate = edits[c.id] ?? c.rate_to_try;
    await api.put(`/admin/currencies/${c.id}`, { code: c.code, name: c.name, symbol: c.symbol, rate_to_try: parseFloat(newRate) });
    toast.success(`${c.code} güncellendi`); refresh();
    setEdits(e => { const x = {...e}; delete x[c.id]; return x; });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="text-sm text-ink-500">Döviz kurları SGS portalından canlı çekilir</div>
        <button data-testid="admin-sync-currencies" onClick={syncFromSGS} disabled={syncing} className="kz-btn-primary">
          <RefreshCw size={14} className={syncing ? "animate-spin" : ""}/> {syncing ? "Çekiliyor..." : "SGS'den Kurları Çek"}
        </button>
      </div>
      <div className="kz-card overflow-x-auto">
        <table className="w-full">
          <thead className="bg-ink-100">
            <tr className="text-left text-xs uppercase tracking-wider">
              <th className="p-3">Kod</th><th className="p-3">Ad</th><th className="p-3">Sembol</th>
              <th className="p-3">Kur (1 birim = ? TRY)</th><th className="p-3">Son Güncelleme</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {currencies.map((c, i) => (
              <tr key={c.id} className={i % 2 ? "bg-ink-100/50" : ""}>
                <td className="p-3 font-bold">{c.code}</td>
                <td className="p-3 text-sm">{c.name}</td>
                <td className="p-3">{c.symbol}</td>
                <td className="p-3">
                  <input type="number" step="0.0001" value={edits[c.id] ?? c.rate_to_try}
                    onChange={(e)=>setEdits({...edits, [c.id]: e.target.value})}
                    disabled={c.code === "TRY"} className="border border-ink-200 px-2 py-1 w-32 font-mono"/>
                </td>
                <td className="p-3 text-xs text-ink-500">{new Date(c.updated_at).toLocaleString("tr-TR")}</td>
                <td className="p-3">
                  {edits[c.id] !== undefined && edits[c.id] !== c.rate_to_try.toString() && (
                    <button onClick={() => save(c)} className="kz-btn-primary text-xs py-1.5 px-3"><Save size={12}/> Kaydet</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PaymentSettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/stripe-settings").then(r => setS(r.data)); }, []);

  if (!s) return <div className="text-ink-500">Yükleniyor...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/stripe-settings", s);
      toast.success("Ödeme ayarları kaydedildi");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="kz-card p-6 space-y-5">
        <div>
          <h3 className="font-display font-black text-lg mb-1">Kredi Kartı Ödemeleri (Stripe)</h3>
          <p className="text-sm text-ink-500">Müşteriden alınan kredi kartı ödemeleri Stripe üzerinden işlenir. Bu bilgiler yalnızca yönetim/muhasebe amaçlıdır — para transferi Stripe hesap ayarlarınızdan yapılır.</p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 p-3 text-xs text-yellow-900">
          <b>Nasıl çalışır?</b> Stripe müşteri kartından tahsilatı yapar, tutarı Stripe hesabınızda tutar ve düzenli aralıklarla (2-7 iş günü) tanımladığınız banka hesabınıza otomatik gönderir (payout).<br/>
          Şu an <b>{s.api_key_mode === "test" ? "TEST" : "CANLI"}</b> modda. Gerçek para akışı için <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="underline">Stripe Dashboard</a>'da hesabınızı aktive edip banka hesabınızı eklemelisiniz.
        </div>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={s.enabled} onChange={(e)=>setS({...s, enabled: e.target.checked})}/>
          <span className="text-sm font-semibold">Kredi kartı ile ödemeyi etkinleştir</span>
        </label>

        <label className="block">
          <span className="text-sm font-semibold mb-1 block">Firma Ünvanı (Stripe hesabındaki)</span>
          <input data-testid="stripe-company" value={s.company_name || ""} onChange={(e)=>setS({...s, company_name: e.target.value})}
                 placeholder="Örn: Kuzey Yapı Hırdavat" className="w-full border border-ink-200 px-3 py-2"/>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-semibold mb-1 block">Payout IBAN</span>
            <input data-testid="stripe-iban" value={s.beneficiary_iban || ""} onChange={(e)=>setS({...s, beneficiary_iban: e.target.value})}
                   placeholder="TR__ ____ ____ ____ ____ __" className="w-full border border-ink-200 px-3 py-2 font-mono text-sm"/>
          </label>
          <label className="block">
            <span className="text-sm font-semibold mb-1 block">Banka Adı</span>
            <input value={s.beneficiary_bank || ""} onChange={(e)=>setS({...s, beneficiary_bank: e.target.value})}
                   placeholder="Örn: Ziraat Bankası" className="w-full border border-ink-200 px-3 py-2"/>
          </label>
        </div>

        <label className="block">
          <span className="text-sm font-semibold mb-1 block">API Mod</span>
          <select value={s.api_key_mode} onChange={(e)=>setS({...s, api_key_mode: e.target.value})} className="w-full border border-ink-200 px-3 py-2">
            <option value="test">TEST — Gerçek para transferi yok</option>
            <option value="live">CANLI — Gerçek tahsilat (Stripe hesap onayı gerekli)</option>
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-semibold mb-1 block">Muhasebe Notu</span>
          <textarea value={s.payout_note || ""} onChange={(e)=>setS({...s, payout_note: e.target.value})}
                    rows={2} className="w-full border border-ink-200 px-3 py-2"/>
        </label>

        <button data-testid="save-stripe-settings" onClick={save} disabled={saving} className="kz-btn-primary">
          <Save size={14}/> {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </button>
      </div>
    </div>
  );
}

function SyncTab({ jobs, logs, refresh }) {
  const [running, setRunning] = useState(false);

  useEffect(() => {
    let t;
    const poll = async () => {
      const r = await api.get("/admin/sync-status");
      setRunning(r.data.running);
      if (r.data.running) t = setTimeout(poll, 5000);
      else if (running) { refresh(); toast.success("Senkron tamamlandı"); }
    };
    poll();
    return () => clearTimeout(t);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncNow = async () => {
    if (!window.confirm("SGS'den tüm ürünler güncellensin mi? Yeni/değişen görseller sunucumuza indirilir. İşlem arka planda 1-3 dakika sürer.")) return;
    try {
      const r = await api.post("/admin/sync-sgs", null, { timeout: 30000 });
      toast.success(r.data.message || "Senkron başlatıldı");
      setRunning(true); refresh();
    } catch (err) {
      toast.error("Senkron hatası: " + (err?.response?.data?.detail || err.message));
    }
  };
  const syncing = running;

  return (
    <div className="space-y-6">
      <div className="kz-card p-5">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <h3 className="font-display font-black mb-2">SGS B2B Otomatik Senkron</h3>
            <p className="text-sm text-ink-500">Her gün 10:00 ve 23:00'da (Europe/Istanbul) SGS B2B portalına otomatik bağlanır, döviz kurları ve tüm ürünleri günceller.</p>
          </div>
          <button data-testid="admin-sync-now" onClick={syncNow} disabled={syncing} className="kz-btn-primary">
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""}/> {syncing ? "Senkron çalışıyor..." : "Şimdi Senkronize Et"}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-ink-200">
          <h4 className="font-semibold text-sm mb-2">Programlı Görevler (Europe/Istanbul)</h4>
          {jobs.length === 0 ? <div className="text-sm text-ink-500">Programlı görev yok</div> : (
            <div className="space-y-1">
              {jobs.map(j => (
                <div key={j.id} className="flex justify-between text-sm">
                  <span className="font-semibold flex items-center gap-2"><Calendar size={14} className="text-brand"/>{j.name}</span>
                  <span className="text-ink-500 font-mono text-xs">{j.next_run}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="font-display font-black mb-3">Senkron Geçmişi</h3>
        <div className="kz-card divide-y divide-ink-200 max-h-[400px] overflow-y-auto">
          {logs.length === 0 ? <div className="p-10 text-center text-ink-500">Henüz senkron yapılmamış</div> :
            logs.map(l => (
              <div key={l.id} className="p-3 flex justify-between text-sm gap-3 flex-wrap">
                <div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 ${l.type.includes("failed") ? "bg-red-100 text-red-700" : l.type.includes("started") ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>{l.type}</span>
                  {l.products !== undefined && <span data-testid="sync-log-summary" className="ml-3">{l.products} ürün · {l.categories} kategori
                    {l.inserted !== undefined && <span className="text-ink-500"> · {l.inserted} yeni · {l.updated} güncel · {l.removed} kaldırıldı · Görsel: {l.images_uploaded} indirildi, {l.images_reused} mevcut{l.images_failed ? `, ${l.images_failed} hatalı` : ""}</span>}
                  </span>}
                  {l.error && <span className="ml-3 text-red-600 text-xs">{l.error}</span>}
                </div>
                <span className="text-xs text-ink-500 font-mono">{new Date(l.at).toLocaleString("tr-TR")}</span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}
